import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, Sparkles, Clock } from 'lucide-react';
import {
  FaInstagram,
  FaLinkedin,
  FaTwitter,
  FaGithub,
  FaFacebook,
  FaYoutube,
} from 'react-icons/fa';
import PageHero from '../components/PageHero';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
    setForm({ name: '', email: '', message: '' });
  };

  const contactInfo = [
    { icon: Mail, label: 'Email', value: 'isnapmedia28@gmail.com', link: 'mailto:isnapmedia28@gmail.com' },
    { icon: Phone, label: 'Phone', value: '+233245258723 | +233574047270', link: 'tel:+233245258723' },
    { icon: MapPin, label: 'Location', value: 'West Adenta | GM-042-8370', link: null },
  ];

  const socialLinks = [
    { icon: FaFacebook, href: 'https://www.facebook.com/isnapmedia_solutions', label: 'Facebook' },
    { icon: FaInstagram, href: 'https://www.instagram.com/isnapmedia_solutions', label: 'Instagram' },
    { icon: FaLinkedin, href: 'https://linkedin.com/in/isnapmedia_solutions', label: 'LinkedIn' },
    { icon: FaTwitter, href: 'https://x.com/isnapmedia_solutions', label: 'Twitter' },
    { icon: FaYoutube, href: 'https://youtube.com/user/isnapmedia_solutions', label: 'Youtube' },
  ];

  return (
    <section className="min-h-screen py-24 bg-gradient-to-b from-black via-zinc-900 to-black pt-32 relative overflow-hidden">
      {/* ─── Background decorations ─── */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-yellow-600/20 via-transparent to-transparent pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-yellow-600/10 blur-3xl animate-pulse" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-yellow-600/10 blur-3xl animate-pulse delay-1000" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* ─── Page Hero ─── */}
        <PageHero
          badge="Contact"
          title="Let's <span class='text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-500'>Create</span> Together"
          subtitle="Have a project in mind? Reach out and let's bring your vision to life – I'd love to hear from you."
        />

        <div className="grid lg:grid-cols-3 gap-8">
          {/* ─── Contact Info ─── */}
          <div className="lg:col-span-1 space-y-6">
            {contactInfo.map((info) => {
              const Icon = info.icon;
              const content = (
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Icon size={20} className="text-yellow-400" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider">{info.label}</div>
                    <div className="text-white font-medium">{info.value}</div>
                  </div>
                </div>
              );
              return (
                <div
                  key={info.label}
                  className="group bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-white/10 hover:border-yellow-500/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-yellow-500/5"
                >
                  {info.link ? (
                    <a href={info.link} className="block">
                      {content}
                    </a>
                  ) : (
                    content
                  )}
                </div>
              );
            })}

            {/* Working hours */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <Clock size={20} className="text-yellow-400" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Working Hours</div>
                  <div className="text-white font-medium">Mon – Fri, 9:00 AM – 6:00 PM</div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-3 pt-2">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-yellow-500/20 hover:border-yellow-500/30 transition-all duration-300"
                    aria-label={social.label}
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* ─── Contact Form ─── */}
          <div className="lg:col-span-2 bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-yellow-500/20 transition-all duration-300">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1.5">
                    Your Name
                  </label>
                  <input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-500 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 outline-none transition"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1.5">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-500 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 outline-none transition"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1.5">
                  Message
                </label>
                <textarea
                  id="message"
                  rows="5"
                  placeholder="Tell me about your project..."
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-500 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 outline-none transition resize-none"
                />
              </div>
              <button
                type="submit"
                className={`w-full sm:w-auto px-8 py-4 rounded-xl bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-medium flex items-center justify-center gap-2 hover:shadow-2xl hover:shadow-yellow-500/30 transition-all duration-300 group ${
                  submitted ? 'bg-green-600 hover:shadow-green-500/30' : ''
                }`}
              >
                {submitted ? (
                  <>
                    <CheckCircle size={20} className="text-green-300" />
                    Message Sent!
                  </>
                ) : (
                  <>
                    Send Message
                    <Send size={18} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
              {submitted && (
                <p className="text-green-400 text-sm mt-2 animate-pulse">
                  Thanks for reaching out! I'll get back to you soon.
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;